import cx from 'classnames';
import { t } from 'i18next';
import _ from 'lodash';
import moment from 'moment';
import momentTimezone from 'moment-timezone';
import { Component } from 'react';
import { Trans } from 'react-i18next';
import { connect } from 'react-redux';
import { LANGUAGES_BY_CODE } from '../../../../../../../../shared/common/languages';
import { TPatronLevel } from '../../../../../../../../shared/common/patron';
import { safeCharacterName } from '../../../../../../../../shared/common/utils';
import LoadableMarkdownRenderer from '../../../../../../../../shared/frontend/components/editors/loadableMarkdownRenderer';
import PatronLabel from '../../../../../../../../shared/frontend/components/patreon/patronLabel';
import { SpecIcon } from '../../../../../../../../shared/frontend/scripts/wow/components/SpecIcon';
import { ComplexTooltip } from '../../../../../../../../shared/frontend/components/presentational/tooltip';
import frontendLog from '../../../../libs/frontendLog';
import {
  TRecruitmentCharacterProfile,
  TRecruitmentGuildProfile,
  TRecruitmentTeamProfile
} from '../../../../libs/store';
import { getTransferTags } from '../../../../recruitmentProfiles/components/TransferRequirements';
import { buildDisplay, isRequirementComplete } from '../../utils';
import { ProfileCombinedSlotsPreview } from '../ProfileCombinedSlotsPreview';
import { RecruitmentActivityIcon } from '../RecruitmentActivityIcon';
import { getGameConfig } from '../../../../../../../../shared/gameConfig';
import { SHOW_PATRON_LABEL_ABOVE_LEVEL } from '../../../../../../shared/enums';
import { RecruitmentEntityType, TRecruitmentProfileTag } from '../../../../../../../../shared/types/recruitmentTypes';
import { getDefaultTimezone } from '../../../../../../../../shared/frontend/libs/utils';
import { RecruitmentProfileSchedulePreview } from '../../../../../../../../shared/frontend/scripts/recruitment/components/RecruitmentProfileSchedulePreview';
import {
  classesSelector,
  racesSelector,
  specsSelector
} from '../../../../../../../../shared/frontend/scripts/selectors/global';
import { asset } from '../../../../../../../../shared/frontend/services/assetResolverService';
import { ModerationReportButton } from '../../../../../../../../shared/frontend/components/moderation/ModerationReportButton';
import { ModerationReportContextType } from '../../../../../../../../shared/common/moderationTypes';

const gameConfig = getGameConfig();

const styles = {
  calloutContainer: 'character-recruitment-preview--calloutContainer',
  classIcon: 'character-recruitment-preview--classIcon',
  recruitEmptyPanel: 'character-recruitment-preview--recruitEmptyPanel',
  recruitPanel: 'character-recrutment-preview--recruitPanel',
  recruitPanelContent: 'character-recruitment-preview--recruitPanelContent',
  recruitPanelHeader: 'character-recruitment-preview--recruitPanelHeader',
  recruitPanelIcon: 'character-recruitment-preview--recruitPanelIcon',
  roleIcon: 'character-recruitment-preview--roleIcon',
  sectionHeading: 'character-recruitment-preview--sectionHeading',
  scheduleCallout: 'character-recruitment-preview--scheduleCallout',
  scheduleCalloutContainer: 'character-recruitment-preview--scheduleCalloutContainer',
  factionLogo: 'character-recruitment-preview--faction-logo'
};

// forces moment timezone to load and inject itself into moment
momentTimezone.toString();

type TProfilePreviewProps = {
  classes?: any[];
  races?: any[];
  specs?: any[];
  className?: string;
  style?: any;
  // use the timezone of the profile if set; otherwise use viewer's timezone
  useProfileTimezone?: boolean;
  hideAllTooltip?: boolean;
};

type TCharacterTarget = any;
type TGuildTarget = any;

type TStateProps = {
  profileType: string;
  showPatronLabel?: boolean;
  profile: TRecruitmentCharacterProfile | TRecruitmentGuildProfile | TRecruitmentTeamProfile;
  target: TCharacterTarget | TGuildTarget;
};

export type TProfilePreviewCombinedProps = TStateProps & TProfilePreviewProps;

function Callout(props: any) {
  return (
    <div className={`${props.className} ${styles.calloutContainer}`}>
      <b>
        <div className={'slds-text-body--medium'}>{props.value}</div>
      </b>
      <div className={'slds-text-body--small text-muted'}>{props.label}</div>
    </div>
  );
}

export function ProfileCaptionPreview(props: any) {
  // TODO: allow patrons to do images here?
  return (
    <div style={{ overflowWrap: 'break-word' }}>
      <LoadableMarkdownRenderer markdown={`&ldquo;${props.caption}&rdquo;`} allowImages={false} />
    </div>
  );
}

type TProfileHeader = {
  left?: any;
  middle?: any;
  right?: any;
  faction: string;
};

function ProfileHeader(props: TProfileHeader) {
  return (
    <div
      style={{
        position: 'relative',
        backgroundImage: `url(${asset(`/images/profile/masthead_backdrops/v2/${props.faction}banner1.jpg`, {
          width: 800,
          quality: 80
        })})`
      }}
      className={cx(
        styles.recruitPanelHeader,
        'slds-p-around--x-small slds-text-heading--small slds-grid slds-grid--align-spread slds-grid--vertical-align-center'
      )}
    >
      <div className="slds-col slds-grow-none slds-text-body--regular slds-shrink-none">{props.left}</div>
      <div className="slds-col slds-grow slds-text-body--regular">{props.middle}</div>
      <div className="slds-col slds-grid slds-grow-none slds-text-body--regular slds-shrink-none">{props.right}</div>
    </div>
  );
}

class RecruitmentProfilePreview extends Component<TProfilePreviewCombinedProps, any> {
  public render() {
    const { profile, profileType, className } = this.props;
    if (!profile) {
      return null;
    }

    if (profileType === RecruitmentEntityType.Character) {
      return (
        <div style={{ minWidth: 400, maxWidth: 400, ...this.props.style }} className={className}>
          {this.renderCharacterPreview()}
        </div>
      );
    } else if (profileType === RecruitmentEntityType.Guild) {
      return (
        <div style={{ minWidth: 400, maxWidth: 400, ...this.props.style }} className={className}>
          {this.renderGuildPreview()}
        </div>
      );
    } else if (profileType === RecruitmentEntityType.Team) {
      return (
        <div style={{ minWidth: 400, maxWidth: 400, ...this.props.style }} className={className}>
          {this.renderTeamPreview()}
        </div>
      );
    }
  }

  public renderGuildPreview() {
    const { profile, target, hideAllTooltip } = this.props;

    // TODO: fix this foolishness...something to do with loading existing prof
    // vs during creation
    const guild = 'details' in target ? target.details : target;

    const qualifiedProfileTag = gameConfig.QualifiedGuildRecruitmentTags.find((qt) =>
      profile.tags?.some((tag) => qt.tagId === tag.id)
    );
    const patronLevel = this.getPatronLevel();

    return (
      <div className={styles.recruitPanel}>
        <ProfileHeader
          faction={guild.faction}
          right={
            <>
              {qualifiedProfileTag && (
                <ComplexTooltip placement="top" tooltip={t(qualifiedProfileTag.tooltipLocKey)}>
                  <img
                    src={asset(qualifiedProfileTag.badgeSmallImagePath)}
                    className="slds-m-right--xx-small"
                    style={{ height: 20, aspectRatio: qualifiedProfileTag.badgeAspectRatio }}
                  />
                </ComplexTooltip>
              )}
              <RecruitmentActivityIcon hideTooltip={hideAllTooltip} type={profile.activity_type} size="x-small" />
            </>
          }
          left={
            <Trans
              i18nKey={`recruitment:entity-activity-title.${profile.entity_type}.${profile.activity_type}`}
              values={{
                name: guild.name
              }}
            >
              <div className={`d-inline rio-faction-color--${guild.faction}`} />
            </Trans>
          }
        />

        <div className={styles.recruitPanelContent}>
          {this.captionSection()}

          {this.guildProgressSection()}

          {this.recruitingSlotsSection()}

          {this.scheduleSection()}

          {this.requirementsSection()}

          {this.tagsSection()}

          <div className="slds-grid slds-grid--vertical-align-end">
            <div className="slds-size--1-of-5">{this.timezoneTag()}</div>
            <div className="slds-size--3-of-5">{this.languagesSection()}</div>
            <div className="slds-size--1-of-5 slds-grid slds-grid--align-end slds-grid--vertical-align-center">
              {this.props.showPatronLabel && patronLevel?.level > SHOW_PATRON_LABEL_ABOVE_LEVEL && (
                <PatronLabel {...patronLevel} />
              )}
              <ModerationReportButton
                contextType={ModerationReportContextType.RECRUITMENT_LISTING}
                entityId={profile.id}
                variant="icon"
                className="slds-m-left--x-small"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  public renderTeamPreview() {
    const { profile, target, hideAllTooltip } = this.props;

    const team = (profile as TRecruitmentTeamProfile)?.groupCharter?.groupCharter || target;
    const patronLevel = this.getPatronLevel();

    return (
      <div className={styles.recruitPanel}>
        <ProfileHeader
          faction={team.faction}
          left={
            <Trans
              i18nKey={`recruitment:entity-activity-title.${profile.entity_type}.${profile.activity_type}`}
              values={{
                name: team.name
              }}
            >
              <div className={`d-inline rio-faction-color--${team.faction}`} />
            </Trans>
          }
          right={
            <>
              <RecruitmentActivityIcon
                hideTooltip={hideAllTooltip}
                type={profile.activity_type}
                size="x-small"
                className="slds-m-right--x-small"
              />
            </>
          }
        />

        <div className={styles.recruitPanelContent}>
          {this.captionSection()}

          {this.recruitingSlotsSection()}

          {this.scheduleSection()}

          {this.requirementsSection()}

          {this.tagsSection()}

          <div className="slds-grid slds-grid--vertical-align-end">
            <div className="slds-size--1-of-5">{this.timezoneTag()}</div>
            <div className="slds-size--3-of-5">{this.languagesSection()}</div>
            <div className="slds-size--1-of-5 slds-grid slds-grid--align-end slds-grid--vertical-align-center">
              {this.props.showPatronLabel && patronLevel?.level > SHOW_PATRON_LABEL_ABOVE_LEVEL && (
                <PatronLabel {...patronLevel} />
              )}
              <ModerationReportButton
                contextType={ModerationReportContextType.RECRUITMENT_LISTING}
                entityId={profile.id}
                variant="icon"
                className="slds-m-left--x-small"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  public renderCharacterPreview() {
    const { profile, target, hideAllTooltip } = this.props;

    const mainCharacter =
      'characters' in profile
        ? _.find(profile.characters, { ordinal: 0 })!
        : { character: { mainspec_0_id: undefined, offspec_0_id: undefined } };

    // during creation, target is used to get the "main character", but after the profile
    // has been saved, we can pull based on ordinal....should find a way to avoid the
    // need to even use target in the first place.
    const character = target.details || mainCharacter?.character;

    // need to get these differently
    const mainSpec = this.getSpec(_.get(mainCharacter, 'mainspec_0_id'));

    const patronLevel = this.getPatronLevel();

    // don't render this data, still allows the user to delete a profile instead of erroring out in the case something goes wrong.
    if (!character) {
      frontendLog.error('Character not found in profile', { profile, target });
      return null;
    }

    return (
      <div>
        <div className={styles.recruitPanel}>
          <ProfileHeader
            faction={character.faction}
            right={
              <>
                {mainSpec && (
                  <ComplexTooltip
                    hideTooltip={hideAllTooltip}
                    placement="top"
                    tooltip={
                      <span className={`class-color--${character.class.slug}`}>
                        {t(`game:specs.${character.class.slug}.${mainSpec.slug}.name`)}{' '}
                        {t(`game:classes.${character.class.slug}.name`)}
                      </span>
                    }
                  >
                    <span className="slds-avatar slds-avatar--x-small rio-border--light">
                      <SpecIcon class={character.class.slug} spec={mainSpec.slug} />
                    </span>
                  </ComplexTooltip>
                )}

                <RecruitmentActivityIcon
                  hideTooltip={hideAllTooltip}
                  type={profile.activity_type}
                  size="x-small"
                  className="slds-m-left--x-small"
                />
              </>
            }
            left={
              <Trans
                i18nKey={`recruitment:entity-activity-title.${profile.entity_type}.${profile.activity_type}`}
                values={{ name: safeCharacterName(character.name) }}
              >
                <div className={`d-inline class-color--${character.class.id}`} />
              </Trans>
            }
          />

          <div className={styles.recruitPanelContent}>
            {this.captionSection()}

            {this.scheduleSection()}

            {this.requirementsSection()}

            {this.tagsSection()}

            <div className="slds-grid slds-grid--vertical-align-end">
              <div className="slds-size--1-of-5">{this.timezoneTag()}</div>
              <div className="slds-size--3-of-5">{this.languagesSection()}</div>
              <div className="slds-size--1-of-5 slds-grid slds-grid--align-end slds-grid--vertical-align-center">
                {this.props.showPatronLabel && patronLevel?.level > SHOW_PATRON_LABEL_ABOVE_LEVEL && (
                  <PatronLabel {...patronLevel} />
                )}
                <ModerationReportButton
                  contextType={ModerationReportContextType.RECRUITMENT_LISTING}
                  entityId={profile.id}
                  variant="icon"
                  className="slds-m-left--x-small"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  private timezoneTag() {
    const { profile, hideAllTooltip } = this.props;
    const timezone = this.props.useProfileTimezone ? profile.schedule_timezone : getDefaultTimezone();

    const tz = moment.tz(timezone || 'UTC').format('z');
    return (
      <div className="character-recruitment-preview--tz">
        <ComplexTooltip
          hideTooltip={hideAllTooltip}
          placement="top"
          tooltip={`${t('recruitment:tooltip.timezone')} ${timezone || 'UTC'}`}
        >
          <>
            <i className="fa fa-globe slds-m-right--xx-small" />
            {tz}
          </>
        </ComplexTooltip>
      </div>
    );
  }

  private renderTags(tags: any[]) {
    return (
      <div className="slds-text-align--center">
        {tags.map((tag: TRecruitmentProfileTag & { special?: boolean }) => {
          const matchingQualifiedTag = gameConfig.QualifiedGuildRecruitmentTags.find((qt) => qt.tagId === tag.id);
          return (
            <div
              key={tag.id}
              className={cx(
                'rio-rec-tag',
                tag.special ? 'rio-rec-tag--special' : '',
                matchingQualifiedTag?.tagClass || ''
              )}
            >
              {tag.name}
            </div>
          );
        })}
      </div>
    );
  }

  private tagsSection() {
    const { profile } = this.props;
    const specialTags = getTransferTags({
      entity_type: profile.entity_type,
      activity_type: profile.activity_type,
      is_faction_transfer_allowed: profile.is_faction_transfer_allowed,
      is_cross_realm_allowed: profile.is_cross_realm_allowed,
      is_realm_transfer_allowed: profile.is_realm_transfer_allowed
    });
    const allTags = [...(profile.tags ?? []), ...specialTags];
    return <>{allTags.length > 0 && this.renderTags(allTags)}</>;
  }

  private languagesSection() {
    const { profile } = this.props;
    const languages = _.compact(
      [profile.language_0, profile.language_1, profile.language_2].map((code: string) => LANGUAGES_BY_CODE[code])
    ).map((lang) => lang.native);

    if (languages.length === 0) {
      return null;
    }

    return (
      <div className="slds-text-body--small slds-text-align--center slds-m-top--small">
        <i>
          {t('recruitment:tooltip.language', { count: languages.length })} {languages.sort().join(' • ')}
        </i>
      </div>
    );
  }

  private guildProgressSection() {
    return null;

    // TODO: implement this
    /*
    return (
      <div className={styles.scheduleCalloutContainer}>
        <div className={cx(styles.sectionHeading, 'slds-m-bottom--small')}>
          <span className="text-muted text-bold">PROGRESS</span>
        </div>
        <div className={`slds-grid slds-wrap slds-grid--align-center slds-grid--align-space`}></div>
      </div>
    );
    */
  }

  private captionSection() {
    const { profile } = this.props;
    return profile.caption ? (
      <div className="slds-text-body--regular slds-text-align--center slds-p-horizontal--large slds-p-bottom--large">
        <ProfileCaptionPreview caption={profile.caption} />
      </div>
    ) : null;
  }

  private recruitingSlotsSection() {
    const profile = this.props.profile as TRecruitmentGuildProfile;

    const nonEmptySlots = profile.slots.filter((slot) => slot.entries.length > 0).length;
    if (nonEmptySlots === 0) {
      return null;
    }

    return (
      <div className={styles.scheduleCalloutContainer}>
        <div className={cx(styles.sectionHeading, 'slds-m-bottom--small')}>
          <span className="text-muted text-bold">{t('recruitment:tooltip.recruiting')}</span>
        </div>
        <div className={'slds-grid slds-wrap slds-grid--align-center'}>
          <ProfileCombinedSlotsPreview slots={profile.slots} />
        </div>
      </div>
    );
  }

  private requirementsSection() {
    const { profile } = this.props;

    const callouts = [];

    for (const req of profile.requirements) {
      if (isRequirementComplete(req)) {
        const { label, value } = buildDisplay(req);
        callouts.push(<Callout key={req.id} className="slds-col" value={value} label={label} />);
      }
    }

    if (callouts.length === 0) {
      return null;
    }

    return (
      <div className={styles.scheduleCalloutContainer}>
        <div className={cx(styles.sectionHeading, 'slds-m-bottom--small')}>
          <span className="text-muted text-bold">{t('recruitment:tooltip.requirements')}</span>
        </div>
        <div className={'slds-grid slds-wrap slds-grid--align-center slds-grid--align-space'}>{callouts}</div>
      </div>
    );
  }

  private scheduleSection() {
    return <RecruitmentProfileSchedulePreview {...this.props} />;
  }

  private getSpec(specId?: number) {
    if (!specId || specId === 0) {
      return null;
    }
    return this.props.specs?.find((spec: any) => spec.id === specId) || null;
  }

  private getPatronLevel = (): TPatronLevel => {
    return this.props.profile.patronLevel || { level: 0, name: '', slug: '' };
  };
}

function mapStateToProps(state: any) {
  return {
    // this stuff would be a good use case to create a context for
    classes: classesSelector(state),
    races: racesSelector(state),
    specs: specsSelector(state)
  };
}

export default connect(mapStateToProps)(RecruitmentProfilePreview);
